import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import App from "@/App";
import { RouteGuard } from "@features/auth/shared/components";
import { AppShell } from "@shared/layout";
import { LoadingSkeleton, RouteErrorBoundary } from "@shared/components";
import { useAuth } from "@shared/hooks/useAuth";

// Main dashboard component that handles role-based routing
const DashboardRouter = lazy(() => import("@shared/components/dashboard/DashboardRouter").then(m => ({ default: m.DashboardRouter })));
const MESSAGING_ENABLED = import.meta.env.VITE_FEATURE_MESSAGING !== 'false';
const MessagesPage = lazy(() => import("@features/messages"));

// Auth pages
const SignIn = lazy(() => import("@features/auth/pages/sign-in"));
const PatientSignIn = lazy(() => import("@features/auth/pages/patient-sign-in"));
const StaffSignIn = lazy(() => import("@features/auth/pages/staff-sign-in"));
const ForgotPassword = lazy(() => import("@features/auth/pages/forgot-password"));
const ResetPassword = lazy(() => import("@features/auth/pages/reset-password"));
const MagicLinkCallback = lazy(() => import("@features/auth/pages/magic-link-callback"));
const MagicLinkRequired = lazy(() => import("@features/auth/pages/magic-link-required"));
const AuthError = lazy(() => import("@features/auth/pages/auth-error"));
const SetupPassword = lazy(() => import("@features/auth/pages/setup-password"));

// Patient docs portal (token + OTP, no login)
const DocsPortalOtpPage = lazy(() => import("@features/shared/pages/docs-portal/OtpPage"));
const DocsPortalUploadPage = lazy(() => import("@features/shared/pages/docs-portal/UploadPage"));

// Patient pages - Production-grade implementations
const PatientQuotesPage = lazy(() => import("@features/patient/pages/quotes"));
const PatientTravelPage = lazy(() => import("@features/patient/pages/travel"));
const PatientPaymentsPage = lazy(() => import("@features/patient/pages/payments"));
const PatientTreatmentPage = lazy(() => import("@features/patient/pages/treatment"));

// Doctor pages - Production-grade implementations
const DoctorMyDayPage = lazy(() => import("@features/doctor/pages/my-day"));
const DoctorPatientsPage = lazy(() => import("@features/doctor/pages/patients"));
const DoctorQuotesPage = lazy(() => import("@features/doctors/quotes"));
const DoctorCalendarPage = lazy(() => import("@features/doctor/pages/calendar"));
const DoctorTemplatesPage = lazy(() => import("@features/doctor/pages/templates"));
const DoctorPaymentsPage = lazy(() => import("@features/doctor/pages/payments"));
const DoctorInvitationAcceptPage = lazy(() => import("@features/doctor/pages/invitation-accept"));
const DoctorOrganizationManagePage = lazy(() => import("@features/doctor/pages/organization-manage"));
const DoctorProfilePage = lazy(() => import("@features/doctor/pages/profile"));
const DoctorOnboarding = lazy(() => import("@features/doctor/onboarding"));
const OwnershipTransferAcceptPage = lazy(() => import("@features/shared/pages/ownership-transfer-accept"));

// Admin pages - Individual page implementations
const AdminPatientsPage = lazy(() => import("@features/admin/pages/patients"));
const AdminQuotingQueuePage = lazy(() => import("@features/admin/pages/quoting-queue"));
const AdminCalendarPage = lazy(() => import("@features/admin/pages/calendar"));
const AdminLogisticsPage = lazy(() => import("@features/admin/pages/logistics"));
const AdminSalesPage = lazy(() => import("@features/admin/pages/sales"));
const AdminPaymentsPage = lazy(() => import("@features/admin/pages/payments"));
const AdminContractsPage = lazy(() => import("@features/admin/pages/contracts"));
const AdminReportsPage = lazy(() => import("@features/admin/pages/reports"));
const AdminUserManagementPage = lazy(() => import("@features/admin/pages/user-management"));
const AdminTravelPlanningPage = lazy(() => import("@features/quoting-queue/pages/travel-planning/TravelPlanningPage"));

// Role-based component router for quotes
const QuotesRouter = () => {
  const { user } = useAuth();

  if (user?.role === 'doctor' || user?.role === 'admin') {
    return <DoctorQuotesPage />;
  }

  return <PatientQuotesPage />;
};

// Role-based component router for payments
const PaymentsRouter = () => {
  const { user } = useAuth();

  if (user?.role === 'admin') {
    return <AdminPaymentsPage />;
  }

  if (user?.role === 'doctor') {
    return <DoctorPaymentsPage />;
  }

  return <PatientPaymentsPage />;
};

// Role-based component router for calendar
const CalendarRouter = () => {
  const { user } = useAuth();

  if (user?.role === 'admin') {
    return <AdminCalendarPage />;
  }

  return <DoctorCalendarPage />;
};

const messagingRoutes = MESSAGING_ENABLED
  ? [
    {
      path: "/messages",
      element: (
        <RouteGuard>
          <AppShell>
            <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
              <MessagesPage />
            </Suspense>
          </AppShell>
        </RouteGuard>
      ),
    },
    {
      path: "/messages/:roomSlug",
      element: (
        <RouteGuard>
          <AppShell>
            <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
              <MessagesPage />
            </Suspense>
          </AppShell>
        </RouteGuard>
      ),
    },
  ]
  : [
    {
      path: "/messages",
      element: <Navigate to="/dashboard" replace />,
    },
    {
      path: "/messages/:roomSlug",
      element: <Navigate to="/dashboard" replace />,
    },
  ];

const chatRedirectTarget = MESSAGING_ENABLED ? "/messages" : "/dashboard";

// Super Admin pages
const SuperAdminUserManagement = lazy(() => import("@features/super-admin/pages/user-management"));
const SuperAdminOrganizationManagement = lazy(() => import("@features/super-admin/pages/organization-management"));
const SuperAdminOrganizationPlans = lazy(() => import("@features/super-admin/pages/organization-plans"));
const SuperAdminKanban = lazy(() => import("@features/super-admin/pages/super-admin-kanban"));
const SuperAdminLogistics = lazy(() => import("@features/super-admin/pages/super-admin-logistics"));
const SuperAdminBillingManagement = lazy(() => import("@features/super-admin/pages/billing-management"));
const SuperAdminPaymentManagement = lazy(() => import("@features/super-admin/pages/payment-management"));
const SuperAdminAuditLogs = lazy(() => import("@features/super-admin/pages/audit-logs"));
const SuperAdminReportsAnalytics = lazy(() => import("@features/super-admin/pages/reports-analytics"));
const SuperAdminDashboardSettings = lazy(() => import("@features/super-admin/pages/dashboard-settings"));

// Feature pages (shared across roles with different permissions)

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <RouteErrorBoundary />,
  },

  // Public-ish docs portal routes (OTP gated, token-based)
  {
    path: "/docs/:token",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DocsPortalOtpPage />
      </Suspense>
    ),
  },
  {
    path: "/docs/:token/upload",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DocsPortalUploadPage />
      </Suspense>
    ),
  },

  // Main Dashboard - Role-based content loading
  {
    path: "/dashboard",
    element: (
      <RouteGuard>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <DashboardRouter />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },

  // Authentication routes (no AppShell wrapper)
  {
    path: "/auth/signin",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <SignIn />
      </Suspense>
    ),
  },
  {
    path: "/auth/patient/signin",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <PatientSignIn />
      </Suspense>
    ),
  },
  {
    path: "/staff/login",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <StaffSignIn />
      </Suspense>
    ),
  },
  {
    path: "/staff/forgot-password",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: "/auth/reset-password",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <ResetPassword />
      </Suspense>
    ),
  },
  {
    path: "/auth/callback",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <MagicLinkCallback />
      </Suspense>
    ),
  },
  {
    path: "/auth/magic-link-required",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <MagicLinkRequired />
      </Suspense>
    ),
  },
  {
    path: "/auth/error",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <AuthError />
      </Suspense>
    ),
  },
  {
    path: "/auth/setup-password",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <SetupPassword />
      </Suspense>
    ),
  },

  {
    path: "/doctor/invitations/accept",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorInvitationAcceptPage />
      </Suspense>
    ),
  },
  {
    path: "/ownership-transfer/:token",
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <OwnershipTransferAcceptPage />
      </Suspense>
    ),
  },
  {
    path: "/doctor/onboarding/*",
    element: (
      <RouteGuard requiredRoles={["doctor"]}>
        <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
          <DoctorOnboarding />
        </Suspense>
      </RouteGuard>
    ),
  },

  // Feature routes - Role-based access control with shared URLs
  {
    path: "/quotes",
    element: (
      <RouteGuard>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <QuotesRouter />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/patients",
    element: (
      <RouteGuard requiredRoles={["doctor", "admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <DoctorPatientsPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/calendar",
    element: (
      <RouteGuard requiredRoles={["doctor", "admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <CalendarRouter />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/travel",
    element: (
      <RouteGuard requiredRoles={["patient"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <PatientTravelPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/payments",
    element: (
      <RouteGuard>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <PaymentsRouter />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/chat",
    element: <Navigate to={chatRedirectTarget} replace />,
  },
  {
    path: "/treatment",
    element: (
      <RouteGuard requiredRoles={["patient"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <PatientTreatmentPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },

  // Doctor-specific routes
  {
    path: "/my-day",
    element: (
      <RouteGuard requiredRoles={["doctor", "admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <DoctorMyDayPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/templates",
    element: (
      <RouteGuard requiredRoles={["doctor", "admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <DoctorTemplatesPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  ...messagingRoutes,
  {
    path: "/organization/manage",
    element: (
      <RouteGuard requiredRoles={["doctor"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <DoctorOrganizationManagePage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/doctor-profile",
    element: (
      <RouteGuard requiredRoles={["doctor"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <DoctorProfilePage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },

  // Individual Admin routes
  {
    path: "/patients",
    element: (
      <RouteGuard requiredRoles={["admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminPatientsPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/quoting-queue",
    element: (
      <RouteGuard requiredRoles={["admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminQuotingQueuePage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/logistics",
    element: (
      <RouteGuard requiredRoles={["admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminLogisticsPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/sales",
    element: (
      <RouteGuard requiredRoles={["admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminSalesPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/contracts",
    element: (
      <RouteGuard requiredRoles={["admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminContractsPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/reports",
    element: (
      <RouteGuard requiredRoles={["admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminReportsPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/admin/users",
    element: (
      <RouteGuard requiredRoles={["admin", "super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminUserManagementPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/admin/travel-planning/:travelCaseId",
    element: (
      <RouteGuard requiredRoles={["admin", "super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <AdminTravelPlanningPage />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },

  // Super Admin routes
  {
    path: "/super-admin/users",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminUserManagement />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/organizations",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminOrganizationManagement />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/plans",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminOrganizationPlans />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/kanban",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminKanban />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/logistics",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminLogistics />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/billing",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminBillingManagement />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/payments",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminPaymentManagement />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/audit",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminAuditLogs />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/reports",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminReportsAnalytics />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },
  {
    path: "/super-admin/messages",
    element: <Navigate to={chatRedirectTarget} replace />,
  },
  {
    path: "/super-admin/settings",
    element: (
      <RouteGuard requiredRoles={["super_admin"]}>
        <AppShell>
          <Suspense fallback={<LoadingSkeleton variant="dashboard" />}>
            <SuperAdminDashboardSettings />
          </Suspense>
        </AppShell>
      </RouteGuard>
    ),
  },

  // Legacy route redirects to maintain compatibility
  {
    path: "/home",
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: "/overview",
    element: <Navigate to="/dashboard" replace />,
  },

  {
    path: "*",
    element: <RouteErrorBoundary />,
  },
]);
